import React from "react";
import { Heart, Clock, Gift, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface BenefitProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  className?: string;
}

const Benefit = ({ icon, title, description, className }: BenefitProps) => {
  return (
    <div className={cn("flex flex-col items-center text-center card-highlight relative overflow-hidden group p-6", className)}>
      <div className="absolute inset-0 bg-gradient-to-b from-legatia-orange/5 to-legatia-yellow/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      <div className="bg-legatia-red/10 p-4 rounded-full mb-4 relative z-10">
        {icon}
      </div>
      <h3 className="text-lg font-bold mb-2 relative z-10">{title}</h3>
      <p className="text-gray-600 relative z-10">{description}</p>
    </div>
  );
};

const NewBenefits = () => {
  return (
    <section id="benefits" className="section-padding relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-20 right-[20%] w-32 h-32 rounded-full bg-legatia-red/5"></div>
        <div className="absolute top-40 left-[30%] w-24 h-24 rounded-full bg-legatia-orange/5"></div>
        <div className="absolute bottom-20 left-[10%] w-40 h-40 rounded-full bg-legatia-yellow/5"></div>
        <div className="absolute bottom-40 right-[15%] w-28 h-28 rounded-full bg-legatia-green/5"></div>
      </div>
      
      <div className="container mx-auto relative z-10">
        <h2 className="section-title text-center">
          Offrez <span className="gradient-text">un héritage émotionnel</span>
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          <Benefit 
            icon={<Heart className="h-8 w-8 text-legatia-red" />}
            title="Présence émotionnelle durable"
            description="Vos mots et sentiments traversent le temps, créant une connexion authentique qui résiste aux années"
          />
          
          <Benefit 
            icon={<Clock className="h-8 w-8 text-legatia-red" />}
            title="Transmission de valeurs"
            description="Partagez votre sagesse, vos expériences et vos valeurs exactement au moment où ils en auront besoin"
          />
          
          <Benefit 
            icon={<Gift className="h-8 w-8 text-legatia-red" />}
            title="Soutien concret au bon moment"
            description="Un geste financier qui prend de la valeur et arrive précisément quand il peut avoir le plus d'impact"
          />
          
          <Benefit 
            icon={<Users className="h-8 w-8 text-legatia-red" />}
            title="Renforcement des liens familiaux"
            description="Créez un projet collectif qui connecte les générations autour d'un acte d'amour partagé"
          />
        </div>
        
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-10 mb-10">
          <div className="grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3">
              <h3 className="text-2xl font-bold mb-4">Un impact qui résonne à travers le temps</h3>
              <p className="text-gray-700 mb-4">
                Quand une personne reçoit une capsule Legatia, elle ne découvre pas simplement un message ou un cadeau.
                Elle ressent une connexion profonde qui transcende le temps — la preuve tangible que vous avez pensé à elle, 
                prévu ce moment, et choisi d'être présent malgré la distance temporelle.
              </p>
              <p className="text-gray-700">
                C'est ce type de présence intentionnelle qui crée les souvenirs les plus puissants et les liens les plus durables.
              </p>
            </div>
            <div className="md:col-span-2 relative h-full">
              <div className="h-full flex items-center">
                <div className="relative w-full aspect-video">
                  <div className="absolute inset-0 bg-gradient-to-br from-legatia-orange/20 to-legatia-red/20 rounded-lg transform rotate-3"></div>
                  <img 
                    src="/lovable-uploads/bc60b606-e4a0-4a4d-a579-addce9d72782.png" 
                    alt="Jeune femme avec ses grands-parents découvrant une capsule Legatia" 
                    className="rounded-lg shadow-md relative z-10 w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* New savings section */}
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-legatia-light to-white border border-gray-100 rounded-2xl shadow-lg p-8 md:p-10 mb-16">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="bg-white p-3 rounded-full mb-4 shadow-md">
              <Gift className="h-8 w-8 text-legatia-orange" />
            </div>
            <h3 className="text-2xl font-bold">L'épargne pour les moments clés de vie de votre enfant</h3>
          </div>
          <p className="text-gray-700 text-center max-w-3xl mx-auto">
            Inspiré des meilleures pratiques d'épargne européennes, Legatia crée une réserve de valeur émotionnelle et financière 
            qui se débloque aux moments importants de la vie de votre enfant. Comme un plan d'épargne traditionnel 
            prépare l'avenir, Legatia prépare les étapes cruciales de leur vie avec des souvenirs immortalisés.
          </p>
        </div>
        
        <div className="text-center">
          <Button className="btn-primary">Créer votre capsule temporelle émotionnelle</Button>
        </div>
      </div>
    </section>
  );
};

export default NewBenefits;
